import React from "react";
import { Paper, Typography, useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  LabelList,
} from "recharts";
import { formatCurrency } from "../../utils/currency";

// Utility: Returns breakdown per month, grouping by EUR for "money in" mode
function getMonthlyBreakdownForMoneyIn(invoices, year) {
  // Always EUR in money-in view
  const months = Array.from({ length: 12 }, (_, i) => ({
    month: new Date(0, i).toLocaleString("default", { month: "short" }),
    paid_EUR: 0,
    unpaid_EUR: 0,
  }));

  invoices.forEach((inv) => {
    let d;
    if (inv.status === "paid") {
      d = inv.markedAsPaidAt?.toDate?.() || new Date(inv.markedAsPaidAt);
    } else {
      d =
        inv.invoiceDate?.toDate?.() ||
        new Date(inv.invoiceDate || inv.createdAt);
    }
    if (!(d instanceof Date) || isNaN(d.getTime())) return;
    if (d.getFullYear() !== year) return;
    const idx = d.getMonth();

    if (inv.status === "paid") {
      months[idx].paid_EUR += parseFloat(inv.paidAmountEUR || 0);
    } else {
      // Show unpaid in EUR only if available (e.g. for unpaid foreign invoices)
      months[idx].unpaid_EUR +=
        inv.currency === "EUR" ? parseFloat(inv.totalAmount || 0) : 0; // Only show unpaid EUR
    }
  });

  return { months, currencies: ["EUR"] };
}

// Utility: Original accrual logic, for non-money-in view
function getMonthlyBreakdownByCurrency(invoices, year) {
  const currencySet = new Set();
  invoices.forEach((inv) => {
    if (inv.currency) currencySet.add(inv.currency);
  });
  const currencies = currencySet.size ? Array.from(currencySet) : ["EUR"];

  const months = Array.from({ length: 12 }, (_, i) => {
    const m = {
      month: new Date(0, i).toLocaleString("default", { month: "short" }),
    };
    currencies.forEach((currency) => {
      m[`paid_${currency}`] = 0;
      m[`unpaid_${currency}`] = 0;
    });
    return m;
  });

  invoices.forEach((inv) => {
    const d =
      inv.invoiceDate?.toDate?.() || new Date(inv.invoiceDate || inv.createdAt);
    if (!(d instanceof Date) || isNaN(d.getTime())) return;
    if (d.getFullYear() !== year) return;
    const idx = d.getMonth();
    const currency = inv.currency || "EUR";
    months[idx][`${inv.status}_${currency}`] += parseFloat(
      inv.totalAmount || 0
    );
  });

  return { months, currencies };
}

export default function InvoicesMonthByMonthChart({
  invoices = [],
  year = new Date().getFullYear(),
  height = 240,
  viewMode = "accrual", // "accrual" or "cash"
}) {
  const theme = useTheme();

  // "cash" mode = show all as EUR, otherwise by original currency
  const { months: data, currencies } =
    viewMode === "cash"
      ? getMonthlyBreakdownForMoneyIn(invoices, year)
      : getMonthlyBreakdownByCurrency(invoices, year);

  // Colors: customize as needed
  const barColors = {
    paid: theme.palette.success.light || "#4ade80",
    unpaid: theme.palette.warning.light || "#fbbf24",
  };

  // Legend only needs to show Paid EUR/Unpaid EUR in cash mode
  const legendPayload =
    viewMode === "cash"
      ? [
          { value: "Paid EUR", type: "circle", color: barColors.paid },
          { value: "Unpaid EUR", type: "circle", color: barColors.unpaid },
        ]
      : undefined;

  return (
    <Paper
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 3,
        boxShadow: "0 2px 12px #dae8fa12",
        background: "#fff",
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        {viewMode === "cash"
          ? "Month-by-Month Money-In Breakdown (" + year + ")"
          : "Month-by-Month Invoice Breakdown (" + year + ")"}
      </Typography>

      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="month"
            fontSize={13}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            fontSize={13}
            axisLine={false}
            tickLine={false}
            tickFormatter={(val) => val?.toLocaleString()}
          />
          <Tooltip
            formatter={(value, name) => {
              // In "cash" mode, always show as EUR
              if (viewMode === "cash") {
                return [formatCurrency(value, "EUR"), name];
              }
              // Otherwise, use currency in key (name = "Paid GBP")
              const [status, currency] = name.split(" ");
              return [formatCurrency(value, currency), name];
            }}
            labelFormatter={(label) => label}
          />
          <Legend
            verticalAlign="top"
            iconType="circle"
            height={32}
            wrapperStyle={{ paddingBottom: 12 }}
            payload={legendPayload}
          />
          {/* Only show EUR bars in "cash" view */}
          {viewMode === "cash" ? (
            <>
              <Bar
                dataKey="paid_EUR"
                name="Paid EUR"
                fill={barColors.paid}
                radius={[6, 6, 0, 0]}
                maxBarSize={40}
              >
                <LabelList
                  dataKey="paid_EUR"
                  position="top"
                  style={{ fontSize: 10, fill: "rgba(0,0,0,0.7)" }}
                  formatter={(value) =>
                    value > 0
                      ? value.toLocaleString("en-IE", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      : ""
                  }
                />
              </Bar>
              <Bar
                dataKey="unpaid_EUR"
                name="Unpaid EUR"
                fill={barColors.unpaid}
                radius={[6, 6, 0, 0]}
                maxBarSize={40}
              >
                <LabelList
                  dataKey="unpaid_EUR"
                  position="top"
                  style={{ fontSize: 10, fill: "rgba(0,0,0,0.7)" }}
                  formatter={(value) =>
                    value > 0
                      ? value.toLocaleString("en-IE", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      : ""
                  }
                />
              </Bar>
            </>
          ) : (
            // Original multi-currency accrual view
            <>
              {currencies.map((currency) => (
                <Bar
                  key={`paid_${currency}`}
                  dataKey={`paid_${currency}`}
                  name={`Paid ${currency}`}
                  fill={barColors.paid}
                  radius={[6, 6, 0, 0]}
                  maxBarSize={40}
                >
                  <LabelList
                    dataKey={`paid_${currency}`}
                    position="top"
                    style={{ fontSize: 10, fill: "rgba(0,0,0,0.7)" }}
                    formatter={(value) =>
                      value > 0
                        ? value.toLocaleString("en-IE", {
                            style: "currency",
                            currency,
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        : ""
                    }
                  />
                </Bar>
              ))}
              {currencies.map((currency) => (
                <Bar
                  key={`unpaid_${currency}`}
                  dataKey={`unpaid_${currency}`}
                  name={`Unpaid ${currency}`}
                  fill={barColors.unpaid}
                  radius={[6, 6, 0, 0]}
                  maxBarSize={40}
                >
                  <LabelList
                    dataKey={`unpaid_${currency}`}
                    position="top"
                    style={{ fontSize: 10, fill: "rgba(0,0,0,0.7)" }}
                    formatter={(value) =>
                      value > 0
                        ? value.toLocaleString("en-IE", {
                            style: "currency",
                            currency,
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        : ""
                    }
                  />
                </Bar>
              ))}
            </>
          )}
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}
